// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Checkbox, Group, Loader, Paper, SimpleGrid, Stack, Switch, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Consent, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CONSENT_DEFINITIONS,
  CONTACT_CHANNELS,
  type ConsentDefinition,
  FEATURED_CONSENT_KEY,
  findConsentForDefinition,
  getConsentChannels,
  isConsentActive,
  SMS_CONSENT_KEY,
  withChannelsOnConsent,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { IconDownload } from '@tabler/icons-react';
import { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { useRole } from '../../../hooks/useRole';
import { hasPermission } from '../../../utils/permissions';
import { withTenantScope } from '../../../utils/tenant';
import classes from './ConsentsGrid.module.css';

const OTHERS_ORDER = ['informed-consent', 'release-of-information', 'consent-to-market'];

function downloadInformedConsent(consent: Consent, patient: Patient): void {
  const encoded = consent.sourceAttachment?.data;
  if (!encoded) {
    showNotification({ color: 'red', message: 'Full signed agreement is not available for this consent.' });
    return;
  }
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `informed-consent-${(patient.name?.[0]?.family ?? 'patient').toLowerCase()}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const x of a) {
    if (!b.has(x)) {
      return false;
    }
  }
  return true;
}

interface ContactChannelsFormProps {
  channels: Set<string>;
  smsActive: boolean;
  optedOut: boolean;
  readOnly: boolean;
  loading: boolean;
  onChannelToggle: (code: string, checked: boolean) => void;
  onSmsToggle: (checked: boolean) => void;
  onOptOutToggle: (checked: boolean) => void;
}

function ContactChannelsForm({
  channels,
  smsActive,
  optedOut,
  readOnly,
  loading,
  onChannelToggle,
  onSmsToggle,
  onOptOutToggle,
}: ContactChannelsFormProps): JSX.Element {
  const disabled = loading || readOnly;
  return (
    <Group className={classes.formSection} justify="space-between" align="center" wrap="wrap" gap="md">
      <Text fz={16} fw={600} c="black">
        Best method to reach you:
      </Text>
      <Group gap="lg" wrap="wrap">
        <Checkbox
          label="Text Message"
          color="polaris.5"
          checked={smsActive}
          disabled={disabled || optedOut}
          onChange={(e) => onSmsToggle(e.currentTarget.checked)}
        />
        {CONTACT_CHANNELS.map((ch) => (
          <Checkbox
            key={ch.code}
            label={ch.label}
            color="polaris.5"
            checked={channels.has(ch.code)}
            disabled={disabled || optedOut}
            onChange={(e) => onChannelToggle(ch.code, e.currentTarget.checked)}
          />
        ))}
        <Checkbox
          label="Opt Out of All Contact"
          color="polaris.5"
          checked={optedOut}
          disabled={disabled}
          onChange={(e) => onOptOutToggle(e.currentTarget.checked)}
        />
      </Group>
    </Group>
  );
}

interface ConsentCardProps {
  definition: ConsentDefinition;
  active: boolean;
  loading: boolean;
  readOnly: boolean;
  featured?: boolean;
  onToggle: (active: boolean) => void;
  channelsForm?: JSX.Element;
  footer?: JSX.Element;
}

function ConsentCard({
  definition,
  active,
  loading,
  readOnly,
  featured = false,
  onToggle,
  channelsForm,
  footer,
}: ConsentCardProps): JSX.Element {
  const switchEl = (
    <Switch
      checked={active}
      onChange={(e) => onToggle(e.currentTarget.checked)}
      disabled={loading || readOnly}
      aria-label={`Toggle ${definition.title}`}
      size="lg"
      color="polaris.5"
    />
  );
  const titleEl = (
    <Title order={3} fw={700} fz={18} lh="24px" c="black">
      {definition.title}
    </Title>
  );
  return (
    <Paper
      withBorder
      radius="lg"
      className={classes.card}
      style={(theme) => ({
        borderWidth: 2,
        borderColor: active ? (theme.colors.polaris?.[5] ?? '#640040') : theme.colors.gray[3],
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      <Stack gap={12} p="md" style={{ flex: 1, minHeight: 0 }} justify="flex-start">
        {featured ? (
          <Group justify="space-between" align="center" wrap="nowrap" gap="md">
            {titleEl}
            {switchEl}
          </Group>
        ) : (
          <Stack gap={12} align="flex-start">
            {switchEl}
            {titleEl}
          </Stack>
        )}
        <Text fz={13} c="dimmed" style={{ lineHeight: '18px' }}>
          {definition.description}
        </Text>
      </Stack>
      {channelsForm}
      {footer}
    </Paper>
  );
}

export interface ConsentsGridProps {
  patient: Patient;
  consents: Consent[];
  loading: boolean;
  onConsentsUpdated: (consents: Consent[]) => void;
}

export function ConsentsGrid({ patient, consents, loading, onConsentsUpdated }: ConsentsGridProps): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [pendingChannels, setPendingChannels] = useState<Set<string> | null>(null);

  const { featured, others } = useMemo(() => {
    const feat = CONSENT_DEFINITIONS.find((d) => d.key === FEATURED_CONSENT_KEY) ?? null;
    const byKey = new Map(CONSENT_DEFINITIONS.map((d) => [d.key, d]));
    const rest = OTHERS_ORDER.map((k) => byKey.get(k)).filter((d): d is ConsentDefinition => Boolean(d));
    return { featured: feat, others: rest };
  }, []);

  const consentsRef = useRef(consents);
  consentsRef.current = consents;

  const effectiveActive = useCallback(
    (def: ConsentDefinition): boolean => {
      if (def.key in pending) {
        return pending[def.key];
      }
      return isConsentActive(findConsentForDefinition(consents, def));
    },
    [consents, pending]
  );

  const featuredConsent = featured ? findConsentForDefinition(consents, featured) : undefined;
  const effectiveChannels = pendingChannels ?? getConsentChannels(featuredConsent);
  const smsDef = useMemo(() => CONSENT_DEFINITIONS.find((d) => d.key === SMS_CONSENT_KEY), []);
  const smsActive = smsDef ? effectiveActive(smsDef) : false;

  const handleCardToggle = useCallback(
    (def: ConsentDefinition, newActive: boolean) => {
      setPending((prev) => ({ ...prev, [def.key]: newActive }));
      if (def.key === FEATURED_CONSENT_KEY && !newActive) {
        setPendingChannels(new Set());
        if (smsDef) {
          setPending((prev) => ({ ...prev, [smsDef.key]: false }));
        }
      }
    },
    [smsDef]
  );

  const handleChannelToggle = useCallback(
    (code: string, checked: boolean) => {
      if (!featured) {
        return;
      }
      const base =
        pendingChannels ?? new Set(getConsentChannels(findConsentForDefinition(consentsRef.current, featured)));
      const next = new Set(base);
      if (checked) {
        next.add(code);
        if (!effectiveActive(featured)) {
          setPending((prev) => ({ ...prev, [featured.key]: true }));
        }
      } else {
        next.delete(code);
      }
      setPendingChannels(next);
    },
    [featured, pendingChannels, effectiveActive]
  );

  const handleSmsToggle = useCallback(
    (checked: boolean) => {
      if (!smsDef || !featured) {
        return;
      }
      setPending((prev) => {
        const next = { ...prev, [smsDef.key]: checked };
        if (checked && !(featured.key in next ? next[featured.key] : isConsentActive(featuredConsent))) {
          next[featured.key] = true;
        }
        return next;
      });
    },
    [smsDef, featured, featuredConsent]
  );

  const handleOptOutToggle = useCallback(
    (checked: boolean) => {
      if (!featured) {
        return;
      }
      setPending((prev) => {
        const next = { ...prev, [featured.key]: !checked };
        if (checked && smsDef) {
          next[smsDef.key] = false;
        }
        return next;
      });
      if (checked) {
        setPendingChannels(new Set());
      }
    },
    [featured, smsDef]
  );

  const persistChange = useCallback(
    async (
      definition: ConsentDefinition,
      newActive: boolean,
      channels: Set<string> | null,
      working: Consent[]
    ): Promise<Consent[]> => {
      const existing = findConsentForDefinition(working, definition);
      const now = new Date().toISOString();
      const saved = await Sentry.startSpan(
        { op: 'http.client', name: `Provider Record Consent: ${definition.title}` },
        async () => {
          const base: Consent = existing?.id
            ? { ...existing, status: newActive ? 'active' : 'rejected' }
            : withTenantScope({
                resourceType: 'Consent',
                patient: createReference(patient),
                status: newActive ? 'active' : 'rejected',
                scope: definition.scope,
                category: [definition.category],
                policyRule: definition.policyRule,
                dateTime: now,
              });
          const next = channels !== null ? withChannelsOnConsent(base, channels) : base;
          return existing?.id ? medplum.updateResource(next) : medplum.createResource(next);
        }
      );
      return existing?.id ? working.map((c) => (c.id === existing.id ? saved : c)) : [...working, saved];
    },
    [medplum, patient]
  );

  const commit = useCallback(async () => {
    setSaving(true);
    let working = consentsRef.current;
    try {
      for (const def of CONSENT_DEFINITIONS) {
        const isFeatured = def.key === FEATURED_CONSENT_KEY;
        const toggleChange = def.key in pending;
        const channelChange = isFeatured && pendingChannels !== null;
        if (!toggleChange && !channelChange) {
          continue;
        }
        const existing = findConsentForDefinition(working, def);
        const currentActive = isConsentActive(existing);
        const desiredActive = toggleChange ? pending[def.key] : currentActive;
        const currentChannels = getConsentChannels(existing);
        const desiredChannels = channelChange ? pendingChannels : null;
        const channelsDiffer = channelChange && !setsEqual(desiredChannels as Set<string>, currentChannels);
        if (desiredActive === currentActive && !channelsDiffer) {
          continue;
        }
        working = await persistChange(def, desiredActive, desiredChannels, working);
        onConsentsUpdated(working);
      }
      setPending({});
      setPendingChannels(null);
      setEditMode(false);
    } catch (error) {
      Sentry.captureException(error);
      onConsentsUpdated(consentsRef.current);
      showNotification({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setSaving(false);
    }
  }, [pending, pendingChannels, persistChange, onConsentsUpdated]);

  const handleHeaderButton = useCallback(() => {
    if (!editMode) {
      setPending({});
      setPendingChannels(null);
      setEditMode(true);
      return;
    }
    commit().catch(() => undefined);
  }, [editMode, commit]);

  const handleCancel = useCallback(() => {
    setPending({});
    setPendingChannels(null);
    setEditMode(false);
  }, []);

  if (loading) {
    return (
      <Paper withBorder radius="md" shadow="xs" p={20}>
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      withBorder
      radius="md"
      shadow="xs"
      p={20}
      style={{
        borderColor: editMode ? 'var(--mantine-primary-color-filled)' : undefined,
        borderWidth: editMode ? 2 : undefined,
        transition: 'border-color 0.2s, border-width 0.2s',
      }}
    >
      <Stack gap={10}>
        <Group justify="space-between" align="center">
          <Title order={2} fz={24} lh="32px" fw={700}>
            Consents
          </Title>
          {canEdit && (
            <Group gap="xs">
              {editMode && (
                <Button variant="default" size="xs" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              )}
              <Button color="polaris.5" size="xs" loading={saving} onClick={handleHeaderButton} aria-pressed={editMode}>
                {editMode ? 'Save' : 'Edit'}
              </Button>
            </Group>
          )}
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={16}>
          {others.map((def) => (
            <ConsentCard
              key={def.key}
              definition={def}
              active={effectiveActive(def)}
              loading={saving}
              readOnly={!editMode}
              onToggle={(active) => handleCardToggle(def, active)}
              footer={
                def.key === 'informed-consent' && effectiveActive(def) ? (
                  <Group justify="flex-end" px="md" pb="md">
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      leftSection={<IconDownload size={14} />}
                      onClick={() => {
                        const consent = findConsentForDefinition(consentsRef.current, def);
                        if (consent) {
                          downloadInformedConsent(consent, patient);
                        }
                      }}
                    >
                      Download
                    </Button>
                  </Group>
                ) : undefined
              }
            />
          ))}
        </SimpleGrid>
        {featured && (
          <ConsentCard
            key={featured.key}
            definition={featured}
            active={effectiveActive(featured)}
            loading={saving}
            readOnly={!editMode}
            featured
            onToggle={(active) => handleCardToggle(featured, active)}
            channelsForm={
              <ContactChannelsForm
                channels={effectiveChannels}
                smsActive={smsActive}
                optedOut={!effectiveActive(featured)}
                readOnly={!editMode}
                loading={saving}
                onChannelToggle={handleChannelToggle}
                onSmsToggle={handleSmsToggle}
                onOptOutToggle={handleOptOutToggle}
              />
            }
          />
        )}
      </Stack>
    </Paper>
  );
}
